import { Button, Modal } from "react-bootstrap";
import { memo, useEffect, useState } from "react";
import { DownloadOptions, DownloadPayload } from "types";
import { isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { downloadFastStorage, downloadStorage } from "service/gatewayService";
import { get } from "lodash";

interface Props {
    show: boolean;
    downloadType?: DownloadOptions;
    onClose?: () => void;
    onConfirm?: (
        confirmCallback: (data: DownloadPayload) => Promise<void> | void
    ) => Promise<void>;
}

const ConfirmDownloadModalComponent = (props: Props) => {
    const [showModal, setShowModal] = useState(false);
    const { show, downloadType = "email", onClose, onConfirm } = props;
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadOption, setDownloadOption] =
        useState<DownloadOptions>("email");

    const handleConfirmClick = async (e: any) => {
        e.preventDefault();
        setIsDownloading(true);
        onConfirm?.((data) => downloadData(data));
    };

    const downloadData = async (data: DownloadPayload) => {
        const res = await (downloadOption === "fast"
            ? downloadFastStorage(data)
            : downloadStorage(data));

        if (isHttpSuccess(res.status)) {
            if (downloadOption === "email") {
                showSuccessAlert({
                    message:
                        "An email will be sent to you shortly once it is ready to download.",
                });
            } else {
                const fileName = get(res, "headers['content-disposition']")
                    .split("filename=")[1]
                    .replace(/"/gi, "");
                const link = document.createElement("a");
                link.href = global.URL.createObjectURL(res.data);
                link.download = fileName;
                link.click();
                global.URL.revokeObjectURL(link.href);
                showSuccessAlert({
                    message: "Data for the selected range will be downloaded.",
                });
            }
        } else {
            showErrorAlert(
                getAPIError(res, "Unable to download data. Please try again.")
            );
        }
        handleCloseModal();
    };

    const handleCloseModal = () => {
        onClose?.();
        setIsDownloading(false);
    };

    useEffect(() => {
        setShowModal(show);
        setIsDownloading(false);
    }, [show]);

    useEffect(() => {
        setDownloadOption(downloadType);
    }, [downloadType]);

    return (
        <Modal
            centered
            show={showModal}
            onHide={handleCloseModal}
            backdrop="static"
            keyboard="false"
            aria-labelledby="example-modal-sizes-title-sm"
            className="no-header primary"
        >
            <Modal.Body className="text-center mt-3 mb-3">
                <div className="modal-icon-box">
                    <span className="material-icons outlined">
                        help_outline
                    </span>
                </div>
                <h3 className="mb-3">Confirmation</h3>
                {downloadOption === "email" && (
                    <p className="mb-4">
                        By confirming, an email will be sent to your email
                        address with instructions on how to download.
                        <br />
                        Do you want to continue?
                    </p>
                )}
                {downloadOption === "fast" && (
                    <p className="mb-4">
                        By confirming, data will be ready for download shortly.
                        Do you want to continue?
                    </p>
                )}

                <Button variant="danger" onClick={handleCloseModal}>
                    NO
                </Button>
                <Button
                    disabled={isDownloading || !showModal}
                    variant="primary"
                    onClick={handleConfirmClick}
                >
                    YES
                </Button>
            </Modal.Body>
        </Modal>
    );
};

export const ConfirmDownloadModal = memo(ConfirmDownloadModalComponent);
